import { ModalModel } from './model';
import { action, runInAction } from 'mobx';
import { APIController } from '../api/controller';
import { AuthModel } from '../auth/model';
import { UIController } from '../ui/controller';
import { BugsnagController } from '../bugsnag/controller';
import { AuthController } from '../auth/controller';
import { UserController } from '../user/controller';
import { EnvModel } from '../env/model';
import { BasketController } from '../basket/controller';
import {isBrandSponsored, replaceValues} from '../../lib/helpers';
import { PricingController } from '../pricing/controller';
import { ContentModel } from '../content/model';
import { FlashController } from '../flash/controller';
import { RouterModel } from '../router/model';
import {ChannelSchema, TrackSchema} from '../../types/schema';
import {APIError, HttpError} from '../../types';
import wait from '../../lib/wait';
import {APIResponse} from "../api/types";

export class ModalController {

  constructor(
    private model: ModalModel,
    private api: APIController,
    private auth: AuthModel,
    private ui: UIController,
    private bugsnag: BugsnagController,
    private authController: AuthController,
    private user: UserController,
    private env: EnvModel,
    private basket: BasketController,
    private pricing: PricingController,
    private content: ContentModel,
    private flash: FlashController,
    private router: RouterModel
  ) {}

  @action showDownloadModal = async (licenceId: string) => {
    runInAction(() => {
      this.model.downloadLoading = true;
      this.model.downloadAgree = false;
      this.model.downloadShow = true;
      this.model.downloadError = null;
      this.model.licenseIdentity = licenceId;
    });

    const { userId } = this.model;

    let attribution: string;
    let url: string;
    let attempt = 30;
    let downloadLimitReached = false;

    while (attempt > 0) {
      const [audio, licence] = await Promise.all([
        this.api.audio.getLicenceAudio(licenceId),
        this.api.user.getLicence(userId, licenceId),
        this.env.ready
      ]);

      attribution = licence.data.attribution;
      url = audio.url;
      downloadLimitReached = audio.downloadLimitReached;

      if ((attribution && downloadLimitReached) || (attribution && url)) break;

      await wait(1000);
      attempt--;
    }

    if ((attribution && url) || (attribution && downloadLimitReached)) {
      runInAction(() => {
        this.model.downloadURL = url;
        this.model.downloadAttribution = attribution;
        this.model.downloadLoading = false;
        this.model.downloadLimitReached = downloadLimitReached;
      });
    } else {
      this.bugsnag.notifyException(new Error('Cannot generate for license ' + licenceId));
      runInAction(() => {
        this.model.downloadError = 'Our sincerest apologies, something has gone wrong generating your license. Please get in touch and we\'ll get this fixed lickety-split!'
      });
    }
  };

  @action agreeToDownloadTerms = (agree: boolean) => {
    this.model.downloadAgree = agree;
  };

  @action hideDownloadModal = () => {
    this.model.downloadLimitReached = false;
    this.model.downloadShow = false;
  };

  @action showAttributionModal = async (licenceId: string) => {
    runInAction(() => {
      this.model.attributionLoading = true;
      this.model.attributionShow = true;
    });

    const { userId } = this.model;
    const [licence] = await Promise.all([
      this.api.user.getLicence(userId, licenceId),
      this.env.ready
    ]);

    runInAction(() => {
      this.model.attributionAttribution = licence.data.attribution;
      this.model.attributionLoading = false;
    })
  };

  @action hideAttributionModal = () => {
    this.model.attributionShow = false;
  };

  @action showChannelAddModal = () => {
    this.model.addChannelShow = true;
  };

  @action hideChannelAddModal = () => {
    this.model.addChannelSelection = null;
    this.model.addChannelShow = false;
  };

  @action addChannelSearch = async (query: string) => {
    runInAction(() => {
      this.model.addChannelSelection = null;
      this.model.addChannelLoading = true;
    });

    try {
      const channels = await this.api.youtube.searchChannels(query);

      runInAction(() => {
        this.model.addChannelResults = channels.data;
      });
    } catch (e) {

    } finally {
      runInAction(() => {
        this.model.addChannelLoading = false;
      })
    }
  };

  @action addChannelSelect = (channel: ChannelSchema) => {
    this.model.addChannelSelection = channel;
  };

  @action addChannelDeselect = () => {
    this.model.addChannelSelection = null;
  };

  @action addChannelLink = async () => {
    if (!this.model.addChannelSelection) return null;

    runInAction(() => {
      this.model.addChannelError = null;
      this.model.addChannelLinking = true;
    });

    try {
      await this.api.user.addChannel(this.model.userId, this.model.addChannelSelection);
      await this.user.getYoutubeChannels();
      await this.changeChannel(this.model.addChannelSelection);

      this.hideChannelAddModal();
    } catch (e) {
      this.model.addChannelError = ((e as HttpError).body as APIError).error
    } finally {
      runInAction(() => {
        this.model.addChannelLinking = false;
      })
    }
  };

  @action
  showChannelSelectModal = async () => {
    runInAction(() => {
      this.model.selectChannelLoading = true;
      this.model.selectChannelShow = true;
    });

    try {
      const channels = await this.api.user.getYouTubeChannels(this.auth.user.identity);

      runInAction(() => {
        this.model.selectChannelChannels = channels.data;
        this.model.selectChannelLoading = false;
      });
    } catch (e) {
      this.bugsnag.notifyException(e);

      runInAction(() => {
        this.model.selectChannelChannels = [];
        this.model.selectChannelLoading = false;
      });
    }
  };

  @action
  hideChannelSelectModal = () => {
    this.model.selectChannelShow = false;
  };

  @action
  changeChannel = async (channel: ChannelSchema) => {
    runInAction(() => {
      this.model.selectChannelActivating = channel;
      this.model.selectChannelSelecting = true;
    });

    try {
      await this.user.getYoutubeChannel(channel.id);
      await this.api.user.setDefaultChannel(channel.id);
      await this.authController.getSession();

      runInAction(() => {
        this.model.selectChannelShow = false;
      })
    } finally {
      runInAction(() => {
        this.model.selectChannelActivating = null;
        this.model.selectChannelSelecting = false;
      });
    }
  };

  @action
  hideMustReAuthModal = () => {
    this.auth.mustReAuth = false;
  };

  @action
  showLicenseModal = async (track: TrackSchema) => {
    this.model.licenseShow = true;

    if (!this.auth.user && (!this.model.licenseRates || this.model.licenseRates.length === 0)) {
      this.model.licenseLoading = true;

      await this.pricing.getDefaultRates();

      this.model.licenseLoading = false;
    }

    this.model.licenseTrack = track;
  };

  @action
  hideLicenseModal = () => {
    this.model.licenseShow = false;
  };

  @action
  licenseModalSelectRate = (rateId: string) => {
    this.basket.addToBasket(this.model.licenseTrack, isBrandSponsored(rateId));
    this.model.licenseShow = false;
  };

  @action
  showRestrictedModal = async () => {
    this.model.restrictedShow = true;
  };

  @action
  hideRestrictedModal = async () => {
    this.model.restrictedShow = false;
  };

  @action
  showBasketRestrictedModal = async () => {
    this.model.basketRestrictedShow = true;
  };

  @action
  hideBasketRestrictedModal = async () => {
    this.model.basketRestrictedShow = false;
  };

  @action
  showDeleteAccountModal = async () => {
    this.model.deleteAccountShow = true;

    if (!this.model.deleteAccountPageBody.length) {
      this.model.deleteAccountLoading = true;
      await this.env.ready;
      this.model.deleteAccountPageBody = this.content.deleteModalContent;
    }

    this.model.deleteAccountLoading = false;
  };

  @action
  hideDeleteAccountModal = () => {
    this.model.deleteAccountShow = false;
  };

  @action
  submitDeleteAccount = async (reason: string, recommend: string) => {
    try {
      this.model.deleteAccountSaving = true;

      const answers = [
        reason.length > 0 && { reason },
        recommend.length > 0 && { recommend }
      ].filter(Boolean);

      if (answers.length) {
        const responses = answers.reduce((out, resp) => ({ ...out, ...resp }), {});
        await this.api.user.updateSurvey(this.auth.user.identity, 'delete_account', responses)
      }

      const name = this.auth.user.first_name || this.auth.user.name;
      const email = this.auth.user.email;

      await this.api.user.deleteAccount();
      await this.router.push('/');
      await this.authController.logout(false);
      await this.flash.createFromCMS('user_deleted', { name, email });
    } catch (e) {
      this.model.deleteAccountError = ((e as HttpError).body as APIError).error
    } finally {
      this.model.deleteAccountSaving = false;
    }
  };

  @action
  showDownloadAccountDataModal = async () => {
    this.model.downloadAccountDataSuccess = false;
    this.model.downloadAccountDataError = null;
    this.model.downloadAccountDataLoading = false;
    this.model.downloadAccountDataSaving = false;
    this.model.downloadAccountDataShow = true;
  };

  @action
  hideDownloadAccountDataModal = () => {
    this.model.downloadAccountDataShow = false;
  };

  @action
  submitDownloadAccountData = async () => {
    this.model.downloadAccountDataError = null;

    try {
      this.model.downloadAccountDataSaving = true;

      await this.api.user.downloadAccountData();

      this.model.downloadAccountDataSuccess = true;
    } catch (e) {
      this.model.downloadAccountDataError = ((e as HttpError).body as APIError).error
    } finally {
      this.model.downloadAccountDataSaving = false;
    }
  };
}